import { useEffect, useRef, useState } from "react";

/**
 * Two brand glow intensities for the Ace inversion mark:
 *   - "hero"   — large standalone mark (Login / Landing).
 *   - "chrome" — small mark in product chrome (Sidebar / ArchiveLayout).
 * The amber matches the rest of the brand glow (library.orbitlens.io .hero-logo).
 */
const GLOW = {
  hero: "drop-shadow(0 0 32px rgba(245, 158, 11, 0.3)) drop-shadow(0 0 10px rgba(245, 158, 11, 0.18))",
  chrome:
    "drop-shadow(0 0 16px rgba(245, 158, 11, 0.28)) drop-shadow(0 0 6px rgba(245, 158, 11, 0.16))",
} as const;

type Props = {
  className?: string;
  glow: keyof typeof GLOW;
};

/**
 * AceLogoMark — the OrbitLens Ace inversion mark (transparent PNG) with the
 * brand amber glow.
 *
 * Why this is a component and not a bare <img>: the glow is a CSS
 * `drop-shadow` filter on a transparent PNG. On the first (uncached) paint the
 * browser lays out the <img> box before the PNG's alpha bitmap is decoded, so
 * the filter lands on the rectangular bounding box — a faint square flashes
 * behind the logo. It vanishes on reload because the image is then cached and
 * decoded before first paint. We close the gap by gating paint on
 * `img.decode()`: the mark stays invisible until the alpha bitmap is ready,
 * then fades in. No box can render before the mask exists.
 */
export function AceLogoMark({ className, glow }: Props) {
  const ref = useRef<HTMLImageElement>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let alive = true;
    const show = () => alive && setReady(true);
    // decode() resolves once the bitmap is ready to paint without flashing;
    // on reject (decode/load failure) we reveal anyway rather than hide forever.
    el.decode().then(show, show);
    return () => {
      alive = false;
    };
  }, []);

  return (
    <img
      ref={ref}
      src="/logo-ace-inversion.png"
      alt=""
      aria-hidden="true"
      decoding="async"
      className={className}
      style={{
        filter: GLOW[glow],
        opacity: ready ? 1 : 0,
        transition: "opacity 200ms ease-out",
      }}
    />
  );
}
